import React from 'react';
import { Modal, Form, Input, Row, Col, Spin } from 'antd';
import styled from '@emotion/styled';
import { isEmpty } from 'lodash';
import { GameProfileQuestionSummaryResponse } from '../../../types/gameProfileQuestion';

interface GameProfileQuestionSummaryModalProps {
  visible: boolean;
  onCancel: () => void;
  summaryData: GameProfileQuestionSummaryResponse | null;
  loading: boolean;
}

const { TextArea } = Input;

export const GameProfileQuestionSummaryModal: React.FC<GameProfileQuestionSummaryModalProps> = ({
  visible,
  onCancel,
  summaryData,
  loading,
}) => {
  const [form] = Form.useForm();

  const getRespondents = (countValue: number) => {
    if (countValue && countValue === 1) {
      return `1 respondent`;
    }
    return `${countValue} respondents`;
  };

  return (
    <Modal title="Question Summary" visible={visible} onCancel={onCancel} footer={null} width={800} destroyOnClose>
      <Spin spinning={loading}>
        <StyledContainer>
          <Form name="question-summary" form={form} layout="vertical">
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Form.Item label="Question">
                      <TextArea
                        rows={3}
                        value={summaryData?.question?.name || ''}
                        disabled
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <div className="header">Result</div>
                  </Col>
                  <Col span={24} className="poll-container">
                    {!isEmpty(summaryData?.question?.answers) &&
                      summaryData?.question?.answers.map((result, index) => (
                        <div key={result.id || index} className="poll-result">
                          <div className="result-bar">
                            <div className="percentage">{result?.percent ? Math.round(result.percent) : 0}%</div>
                            <div
                              className={
                                result?.percent && Math.round(result.percent) === 100
                                  ? 'result-text-full'
                                  : 'result-text'
                              }
                            >
                              <div className="answer-title">{result?.title}</div>
                              {result?.subTitle && <span className="description">{result.subTitle}</span>}
                            </div>
                            <div
                              className={
                                result?.percent && Math.round(result.percent) === 100
                                  ? 'bar-filled border-right'
                                  : 'bar-filled'
                              }
                              style={{ width: `${result?.percent || 0}%` }}
                            />
                          </div>
                          <div className="respondents">{getRespondents(result?.count || 0)}</div>
                        </div>
                      ))}
                  </Col>
                  <Col span={24}>
                    <div className="total-respondents">
                      Total: {getRespondents(summaryData?.question?.totalAnswer || 0)}
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        </StyledContainer>
      </Spin>
    </Modal>
  );
};

const StyledContainer = styled.div`
  padding: 20px 30px;
  min-height: 400px;

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 15px;
  }

  & .ant-spin-container {
    min-height: 400px;
  }

  & .header {
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 16px;
  }

  & .poll-result {
    margin-bottom: 8px;
    display: flex;
  }

  & .result-bar {
    display: flex;
    align-items: center;
    position: relative;
    width: 60%;
    gap: 8px;
    min-height: 50px;
    background-color: #fff;
    border-radius: 16px;
    backdrop-filter: blur(7px);
    border: 1px solid #d9d9d9;
    box-shadow: 0px 3px 9px rgba(0, 0, 0, 0.15);
  }

  & .bar-filled {
    position: absolute;
    left: 0;
    background-color: #c4c4c4;
    height: 100%;
    border-radius: 15px 0 0 15px;
  }

  & .border-right {
    border-radius: 15px;
  }

  & .percentage {
    position: absolute;
    left: 15px;
    font-weight: 400;
    z-index: 1;
    color: #03a800;
    font-size: 24px;
  }

  & .result-text {
    margin-left: 70px;
    padding: 6px 0;
    width: 80%;
    z-index: 2;
  }

  & .result-text-full {
    margin-left: 85px;
    padding: 10px 0;
    width: 75%;
    z-index: 2;
  }

  & .answer-title {
    display: block;
    font-size: 12px;
    font-weight: bold;
    color: #000;
    margin-bottom: -2px;
  }

  & .description {
    font-size: 12px;
    font-weight: 400;
    color: #000;
  }

  & .respondents {
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    color: #000;
    text-align: right;
    margin-left: 20px;
  }

  & .total-respondents {
    font-weight: 400;
    font-size: 14px;
    margin-top: -10px;
  }

  & .poll-container {
    margin-bottom: 16px;
  }
`;
